import { ColumnDef } from '@tanstack/react-table'
import dayjs from 'dayjs'
import CustomTable from '@common/CustomTable/Table'
import { TextCell } from '@pages/Admin/Reports/Payments/components/Cells/Cells'
import { ChangeHistoryRow } from '@/api/changeHistory'
import { normalizeTiptapValue } from '@/helpers/new/normalizeTiptapValue'
import { getMessageText } from '@/helpers/messageText'
import { getCorrectFilePath } from '@/helpers/new/getCorrectFilePath'
import cn from './index.module.css'
import { Tag } from 'antd'
import { ScheduleChangesCell, ScheduleLessonCell } from './ScheduleHistoryContent'

const entities: Record<string, string> = {
  timetable: 'Серия занятий',
  timetable_report: 'Занятие',
  timetable_properties: 'Параметры расписания',
  timetable_notes: 'Заметка',
  timetable_enrollments: 'Запись на занятие',
  timetable_audience: 'Аудитория',
  journal_timetable: 'Посещаемость',
  journal_grades: 'Оценка',
  journal_columns: 'Колонка журнала',
  journal_column_values: 'Значение колонки',
  journal_additional_columns: 'Дополнительная колонка',
  journal_additional_column_values: 'Значение дополнительной колонки',
  journal_periods: 'Период',
  ktp_journal: 'КТП',
  access_journal: 'Доступ к уроку',
  user: 'Профиль',
  user_profile: 'Данные профиля',
  user_contract: 'Договор',
  profile_field_value: 'Дополнительное поле профиля',
  additional_field: 'Поле журнала',
  additional_fields_data: 'Значение поля журнала',
  final_analytics_field: 'Поле итоговой аналитики',
  presence_food_rule: 'Правило учёта питания',
  presence_food_column: 'День учёта питания',
  presence_food_status: 'Посещение / питание',
  presence_food_extra_column: 'Столбец учёта питания',
  presence_food_extra_value: 'Значение столбца учёта питания',
  home_work: 'ДЗ / КР',
}

const fields: Record<string, string> = {
  name: 'Имя / название',
  file_path: 'Файл договора',
  title: 'Название',
  email: 'Email',
  phone: 'Телефон',
  login: 'Логин',
  custom_id: 'Внешний ID',
  avatar_path: 'Фото',
  roles: 'Роли',
  departments: 'Отделы (ID)',
  franchises: 'Филиалы (ID)',
  parents: 'Родители (ID)',
  children: 'Дети (ID)',
  status: 'Статус',
  is_disabled: 'Отключён',
  deleted_at: 'Дата удаления',
  about: 'О себе',
  birth_date: 'Дата рождения',
  sex: 'Пол',
  city: 'Город',
  address: 'Адрес',
  education: 'Образование',
  experience: 'Опыт',
  notes: 'Заметки',
  parent_name: 'Имя родителя',
  parent_phone: 'Телефон родителя',
  parent_email: 'Email родителя',
  parent_telegram: 'Telegram родителя',
  parent_whatsapp: 'WhatsApp родителя',
  timezone: 'Часовой пояс',
  learning_class: 'Класс обучения',
  age: 'Возраст',
  about_student: 'Об ученике',
  about_parent: 'О родителе',
  url_conference: 'Ссылка педагога',
  url_conference_for_children: 'Ссылка ученика',
  link_to_vk: 'ВКонтакте',
  telebos_room_id: 'Комната ВКС',
  date_conference: 'Дата занятия',
  start_time: 'Начало',
  end_time: 'Окончание',
  duration: 'Длительность',
  speaker_id: 'Педагог',
  attendees: 'Участники',
  groups: 'Группы',
  comment: 'Комментарий',
  comment_to_dz: 'Комментарий к ДЗ',
  lesson_topic: 'Тема занятия',
  link_to_meeting: 'Ссылка педагога',
  link_to_meeting_for_children: 'Ссылка ученика',
  grade: 'Оценка',
  label: 'Подпись оценки',
  weight: 'Вес оценки',
  type: 'Тип',
  color: 'Цвет',
  student_lesson_status: 'Статус ученика на занятии',
  value: 'Значение',
  list: 'Список',
  member_id: 'Ученик (ID)',
  user_id: 'Пользователь (ID)',
  event_report_id: 'Занятие (ID)',
  journal_id: 'Строка журнала (ID)',
  column_id: 'Колонка (ID)',
  field_id: 'Поле (ID)',
  group_id: 'Группа (ID)',
  direction_id: 'Курс (ID)',
  event_id: 'Серия занятий (ID)',
  date: 'Дата',
  end_date: 'Дата окончания',
  period: 'Период',
  visibility: 'Видимость',
  order: 'Порядок',
  audience_id: 'Аудитория (ID)',
  audience_number: 'Аудитория',
  is_rescheduled: 'Перенесено',
  pinned: 'Закреплено',
  isOpen: 'Открытое занятие',
  salary: 'Зарплата',
  is_paid: 'Оплачено',
  topic_name: 'Тема КТП',
  topic_name_override: 'Название темы',
  topic_id: 'Тема (ID)',
  lesson_id: 'Урок (ID)',
  theme_id: 'Тема курса (ID)',
  block_id: 'Блок (ID)',
  plan_date: 'Плановая дата',
  report_id: 'Занятие (ID)',
  reports: 'Привязанные занятия (ID)',
  homework: 'Домашнее задание',
  homework_rich: 'Форматированное ДЗ',
  homework_files: 'Файлы ДЗ (ID)',
  homework_date: 'Дата выдачи ДЗ',
  homework_report_id: 'Занятие выдачи ДЗ (ID)',
  homework_id: 'Домашнее задание (ID)',
  sort_order: 'Порядок строки КТП',
  is_archive: 'Архивный период',
  lesson_type: 'Тип занятия',
  lesson_type_comment: 'Комментарий к типу занятия',
  score: 'Баллы',
  mark: 'Оценка за работу',
  marks: 'Оценки за работу',
  source_entity: 'Источник данных поля',
  source_id: 'Поле для расчёта',
  color_conditions: 'Условия цвета',
}

export const historyValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '—'
  const document = normalizeTiptapValue(value)
  if (document?.type === 'doc') return getMessageText(document) || '—'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

const historyFileValue = (value: unknown, text: string) => {
  if (typeof value !== 'string' || !/^(https?:\/\/|\/(?!\/)|upload\/)/i.test(value)
    || Array.from(value).some(character => character === '\\' || character.charCodeAt(0) < 32)) return text
  return <a href={getCorrectFilePath(value)} target="_blank" rel="noreferrer">{text}</a>
}

const columns: ColumnDef<ChangeHistoryRow>[] = [
  {
    header: 'Дата и время',
    id: 'date',
    size: 160,
    cell: ({ row }) => (
      <TextCell
        text={dayjs.unix(row.original.created_at).format('DD.MM.YYYY HH:mm:ss')}
      />
    ),
  },
  {
    header: 'Сотрудник',
    id: 'actor',
    size: 180,
    cell: ({ row }) => <TextCell text={row.original.actor_name || 'Система'} />,
  },
  {
    header: 'Занятие / объект изменения',
    id: 'entity',
    size: 310,
    cell: ({ row }) => (
      row.original.presentation ? <ScheduleLessonCell value={row.original.presentation} /> :
      <div className={cn.cell}>
        <span>{row.original.context?.is_additional ? 'Колонка журнала' : entities[row.original.entity_type] || row.original.context?.section || 'Объект изменения'}</span>
        {!row.original.context?.section && <span>{row.original.entity_label}</span>}
        {Object.entries(row.original.context ?? {}).filter(([key, value]) =>
          ['student', 'course', 'group', 'date', 'column', 'work', 'topic'].includes(key) && value
        ).map(([key, value]) => <span key={key}>{({student: row.original.category === 'employees' ? 'Сотрудник' : 'Учащийся', course: 'Предмет', group: 'Группа', date: 'Дата', column: 'Колонка', work: 'Работа', topic: 'Тема занятия'} as Record<string, string>)[key]}: {key === 'date' && dayjs(String(value)).isValid() ? dayjs(String(value)).format(String(value).includes(':') ? 'DD.MM.YYYY HH:mm' : 'DD.MM.YYYY') : String(value)}</span>)}
        {row.original.subject_id && !row.original.context?.student && (
          <span>Пользователь №{row.original.subject_id}</span>
        )}
      </div>
    ),
  },
  {
    header: 'Действие',
    id: 'action',
    size: 120,
    cell: ({ row }) => (
      <div className={cn.cell}>
        <Tag className={cn.actionBadge} color={{create: 'green', copy: 'blue', reschedule: 'orange', swap: 'purple', update: 'blue', delete: 'red'}[row.original.presentation?.action || row.original.action]}>
          {row.original.presentation?.action_label || row.original.context?.action_label || ({create: 'Создание', copy: 'Копирование', reschedule: 'Перенос', swap: 'Перестановка', update: 'Изменение', delete: 'Удаление'}[row.original.action] || 'Изменение')}
        </Tag>
      </div>
    ),
  },
  {
    header: 'Изменения: было → стало',
    id: 'changes',
    size: 480,
    cell: ({ row }) => (
      row.original.presentation ? <ScheduleChangesCell value={row.original.presentation} /> :
      <div className={cn.cell}>
        {Object.entries(row.original.changes).map(([field, change]) => {
          const before = historyValue(row.original.context?.value_labels?.[field]?.[String(change.old)] ?? change.old)
          const after = historyValue(row.original.context?.value_labels?.[field]?.[String(change.new)] ?? change.new)
          return <div key={field}>
            <strong>{row.original.context?.field_labels?.[field] || fields[field] || field}:</strong>{' '}
            {row.original.entity_type === 'user_contract' && field === 'file_path' ? historyFileValue(change.old, before) : before} → {row.original.entity_type === 'user_contract' && field === 'file_path' ? historyFileValue(change.new, after) : after}
            {field.endsWith('_rich') && before === after && <div>Изменено оформление, текст сохранён.</div>}
          </div>
        })}
      </div>
    ),
  },
  {
    header: 'Раздел',
    id: 'source',
    size: 200,
    cell: ({ row }) => <TextCell text={row.original.context?.section || (row.original.presentation ? 'Расписание' : ({schedule: 'Расписание', journal: 'Электронный журнал', ktp: 'КТП', students: 'Карточка ученика', employees: 'Карточка сотрудника'}[row.original.category ?? 'students']))} />,
  },
]

const HistoryTable = ({
  rows,
  loading = false,
}: {
  rows: ChangeHistoryRow[]
  loading?: boolean
}) => (
  <CustomTable<ChangeHistoryRow>
    columns={columns}
    data={rows}
    loading={loading}
    compact
    stickyHeader
    align="left"
  />
)

export default HistoryTable
